import { Input } from "design-system/atoms/Input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { UpdateCampaignSchemaFrontend } from "modules/campaigns/hooks/useCampaignForm"
import { useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  getEmailCampaignChannelIntegration,
  ShopifyCampaignTrigger,
  ShopifyCampaignTriggerEnum,
} from "schema"
import { z } from "zod"

type Props = {
  isDisabled: boolean
}

type FormValues = z.infer<typeof UpdateCampaignSchemaFrontend>
type ShopifyTrigger = Extract<NonNullable<FormValues["triggerIntegration"]>, { type: "shopify" }>

const SHOPIFY_TRIGGER_OPTIONS = [
  ShopifyCampaignTriggerEnum.AbandonCheckout,
  ShopifyCampaignTriggerEnum.OrderFulfilled,
] as const

const SHOPIFY_APP_URL = "https://admin.shopify.com/apps/permutai-sy/app"

export function ShopifyTriggerForm({ isDisabled }: Props) {
  const { t } = useTranslation()
  const form = useFormContext<FormValues>()
  const triggerIntegration = form.watch("triggerIntegration")
  const shopify = triggerIntegration?.type === "shopify" ? triggerIntegration : undefined
  const email = getEmailCampaignChannelIntegration(triggerIntegration)
  const buildShopify = (patch: Partial<ShopifyTrigger>): ShopifyTrigger => ({
    type: "shopify",
    trigger: shopify?.trigger ?? ShopifyCampaignTriggerEnum.AbandonCheckout,
    delayInDays: shopify?.delayInDays ?? undefined,
    email: shopify?.email ?? email,
    ...patch,
  })

  return (
    <>
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => (
          <FormItem className="flex flex-col gap-2 w-full md:max-w-[70vw]">
            <FormLabel>{t("campaigns.trigger.metadata.shopify.trigger.label")}</FormLabel>
            <FormControl>
              <Select
                value={shopify?.trigger}
                onValueChange={(value) => {
                  field.onChange(buildShopify({ trigger: value as ShopifyCampaignTrigger }), {
                    shouldDirty: true,
                  })
                }}
                disabled={isDisabled}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t("campaigns.trigger.metadata.shopify.trigger.placeholder")}
                  />
                </SelectTrigger>
                <SelectContent>
                  {SHOPIFY_TRIGGER_OPTIONS.map((trigger) => (
                    <SelectItem key={trigger} value={trigger}>
                      {t(`campaigns.trigger.metadata.shopify.trigger.options.${trigger}.label`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            {shopify?.trigger && (
              <div className="flex flex-col gap-1">
                <p className="text-sm text-muted-foreground">
                  {t(
                    `campaigns.trigger.metadata.shopify.trigger.options.${shopify.trigger}.description`,
                  )}{" "}
                  {t("campaigns.trigger.metadata.shopify.webhookActivation.description")}{" "}
                  <a className="underline" href={SHOPIFY_APP_URL} target="_blank" rel="noreferrer">
                    {t("campaigns.trigger.metadata.shopify.webhookActivation.openApp")}
                  </a>
                </p>
              </div>
            )}
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="triggerIntegration"
        render={({ field }) => (
          <FormItem className="flex flex-col gap-1 w-full md:max-w-[70vw]">
            <FormLabel>{t("campaigns.trigger.metadata.shopify.delayInDays.label")}</FormLabel>
            <p className="text-sm text-muted-foreground">
              {shopify?.trigger === ShopifyCampaignTriggerEnum.AbandonCheckout
                ? t("campaigns.trigger.metadata.shopify.delayInDays.abandonCheckoutDescription")
                : t("campaigns.trigger.metadata.shopify.delayInDays.description")}
            </p>
            <FormControl>
              <Input
                className="w-32"
                wrapperClassName="w-auto"
                disabled={isDisabled || !shopify?.trigger}
                value={shopify?.delayInDays ?? ""}
                min={0}
                type="number"
                placeholder={t("campaigns.trigger.metadata.shopify.delayInDays.placeholder")}
                rightText={t("campaigns.trigger.metadata.shopify.delayInDays.suffix")}
                onChange={(value) => {
                  const delayInDays = value.target.value ? Number(value.target.value) : undefined

                  field.onChange(buildShopify({ delayInDays }), { shouldDirty: true })
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  )
}
